import {
  getHistoryDialogMessages,
  normalizeMessageTimestamp
} from '@secca/shared/helpers/atlas/atlas-chatbot-mappers.helper';
import { ChatMessage } from '@secca/shared/models/atlas/atlas-chatbot.model';
import { AtlasChatHistorySession } from '@secca/shared/models/atlas/atlas-chat-history.model';

export function normalizeChatHistorySession(session: AtlasChatHistorySession): AtlasChatHistorySession {
  const legacyCaseNumbers = (session as AtlasChatHistorySession & { caseNumbersUsedInContext?: string[] }).caseNumbersUsedInContext || [];
  const inferredCaseNumber = session.caseNumber || legacyCaseNumbers[0] || getFirstLegacyCaseNumber(session.messages) || '-';

  return {
    ...session,
    caseNumber: inferredCaseNumber,
    messages: getHistoryDialogMessages(normalizeChatMessages(session.messages || []))
  };
}

export function normalizeChatMessages(messages: ChatMessage[]): ChatMessage[] {
  return (messages || []).map(message => {
    const legacyCaseContextCaseNumber = (message as ChatMessage & { caseContextCaseNumber?: boolean | string | null }).caseContextCaseNumber;
    const caseContextWasUsed = typeof legacyCaseContextCaseNumber === 'boolean'
      ? legacyCaseContextCaseNumber
      : !!legacyCaseContextCaseNumber;

    return {
      ...message,
      timestamp: normalizeMessageTimestamp(message.timestamp),
      caseContextCaseNumber: caseContextWasUsed,
      caseContextModeEnabled: message.caseContextModeEnabled ?? caseContextWasUsed
    };
  });
}

export function getChatHistorySessionSortTime(session: AtlasChatHistorySession): number {
  const latestMessageTimestamp = (session?.messages || [])
    .map(message => message.timestamp)
    .filter((timestamp): timestamp is string => !!timestamp && !isNaN(new Date(timestamp).getTime()))
    .sort((first, second) => new Date(second).getTime() - new Date(first).getTime())[0];
  const parsed = new Date(latestMessageTimestamp || session?.createdAt || '').getTime();
  return isNaN(parsed) ? 0 : parsed;
}

export function isSameChatHistorySession(first: AtlasChatHistorySession, second: AtlasChatHistorySession): boolean {
  if (!first || !second) {
    return false;
  }

  if (first.id && second.id && first.id === second.id) {
    return true;
  }

  const firstSessionId = first.sessionId && first.sessionId !== '-' ? first.sessionId : null;
  const secondSessionId = second.sessionId && second.sessionId !== '-' ? second.sessionId : null;
  return !!firstSessionId && firstSessionId === secondSessionId;
}

export function getFirstLegacyCaseNumber(messages: ChatMessage[] = []): string | null {
  const legacyMessage = messages.find(message => {
    const value = (message as ChatMessage & { caseContextCaseNumber?: boolean | string | null }).caseContextCaseNumber;
    return typeof value === 'string' && !!value;
  }) as (ChatMessage & { caseContextCaseNumber?: string | null }) | undefined;

  return legacyMessage?.caseContextCaseNumber || null;
}
